import React, { useEffect, useState } from 'react';

import PersonIcon from '@mui/icons-material/Person';
import { Box, Container, Typography } from '@mui/material';
import { APIEndpoint } from 'common/enum';
import {
  universalGetRequest,
  universalPostRequest,
} from 'common/requestHandler';
import { userTypes } from 'config/config';
import { jwtDecode } from 'jwt-decode';
import { popupNotification } from 'notifications/ToastNotification';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'redux/store';

import logo from '../../assets/images/logo.png';
import { login } from '../../redux/slices/authSlice';
import { setIdTokenData } from '../../redux/slices/idTokenSlice';
import GradientButton from '../elements/GradientButton';
import OTPInput from '../OTPInput';
import { JWTDecodedIDToken, validateMFAInterface } from './utility';

const VerifyMFA = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoURL, setLogoURL] = useState<string>('');
  const { username, isFirstLogin, isMfaActive, sessionKey, role, companyId } =
    useSelector((state: RootState) => state.auth);

  const getLogoURL = async () => {
    const response: any = await universalGetRequest(APIEndpoint.getLogo, {
      folderKey: `${companyId}/media/logo.png`,
    });
    if ('error' in response) {
      popupNotification(response.error, false);
    } else {
      setLogoURL(response.data);
    }
  };

  useEffect(() => {
    if (companyId) {
      getLogoURL();
    }
  }, [companyId]);

  const handleMFAVerify = async () => {
    setLoading(true);
    const response = await universalPostRequest<validateMFAInterface>(
      APIEndpoint.validateMFAOTP,
      {
        is_first_login: isFirstLogin,
        is_mfa_active: isMfaActive,
        otp: input,
        username: username,
        session_key: sessionKey,
      }
    );
    setLoading(false);

    if ('error' in response) {
      popupNotification(response.error, false);
    } else {
      const decodedData: JWTDecodedIDToken = jwtDecode(response.data.id_token);
      const tokenData = {
        authTime: decodedData.auth_time,
        phoneNumber: decodedData.phone_number,
        email: decodedData.email,
        username: decodedData.username,
        employeeId: decodedData['custom:employee-id'],
        companyId: decodedData['custom:company-id'],
        corporateId: decodedData['custom:corporate-id'],
        customerId: decodedData['custom:customer-id'],
        emailVerified: decodedData.email_verified,
      };
      const data = {
        accessToken: response.data.access_token,
        expiresIn: response.data.expires_in,
        idToken: response.data.id_token,
        refreshToken: response.data.refresh_token,
        role: decodedData['custom:user-type'],
        employeeId: decodedData['custom:employee-id'],
        customerId: decodedData['custom:customer-id'],
        corporateId: decodedData['custom:corporate-id'],
        companyId: decodedData['custom:company-id'],
        firstName: decodedData['given_name'],
        lastName: decodedData['family_name'],
        isLoggedIn: true,
        lastPasswordChangeDate: decodedData['custom:password_changed_dt'],
        isSignVerifyEnabled:
          decodedData['custom:sign-verify-enabled'] === 'True' ? true : false,
      };
      dispatch(login(data));
      dispatch(setIdTokenData(tokenData));
      if (decodedData['custom:user-type'] === userTypes.superAdmin) {
        navigate('/dashboard');
      } else {
        navigate(`/${tokenData.companyId}/dashboard`);
      }
    }
  };

  return (
    <React.Fragment>
      <Container
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 5,
          height: '100vh',
          flexDirection: 'column',
        }}
      >
        <Box
          minWidth="50%"
          sx={{
            background: `linear-gradient(135deg, rgba(66, 211, 146, 0.15) 0%, rgba(100, 126, 255, 0.15) 100%)`,
            boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)',
            p: 5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 5,
          }}
        >
          {/* Logo */}
          {
            <Box display="flex" justifyContent="center" mb={1}>
              <img src={logo} height={120} alt="Company Logo" />
            </Box>
          }

          <Typography
            variant="h5"
            component="h1"
            sx={{
              color: '#2f3542',
              fontWeight: '600',
              mb: 2,
            }}
          >
            Enter code from authenticator app
          </Typography>
          <form style={{ width: '100%' }}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={4}
              width="100%"
            >
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                <PersonIcon sx={{ color: '#647eff' }} fontSize="large" />
                <Typography variant="body1" sx={{ color: '#2f3542' }}>
                  {username}
                </Typography>
              </Box>
              <OTPInput
                length={6}
                onChange={setInput}
                onComplete={setInput}
                autoFocus
                disabled={loading}
              />
              <GradientButton
                rounded
                fullWidth
                type="submit"
                disabled={input.length !== 6}
                loading={loading}
                onClick={() => {
                  handleMFAVerify();
                }}
              >
                Verify
              </GradientButton>
            </Box>
          </form>
        </Box>
      </Container>
    </React.Fragment>
  );
};

export default VerifyMFA;
